// Marketing analytics dispatcher — Google Analytics 4 (gtag) + Meta Pixel (fbq).
//
// Design contract:
//   - SSR-safe. Every exported tracker no-ops when `window` is undefined or
//     the vendor tag never loaded (IDs not configured / blocked by an ad
//     blocker). Nothing here throws; analytics must never break a payment.
//   - Public, build-time IDs. The GA4 measurement id and Meta pixel id are
//     NOT secrets — they ship in the client bundle. They are read from Vite
//     env at BUILD time (same pattern as VITE_SENTRY_DSN, configured in
//     Cloudflare Workers Builds → Settings → Build → Variables). When either
//     is absent the corresponding vendor is simply never injected, so this
//     whole feature degrades to a no-op with the env unset.
//   - Fire-once semantics. `purchase`, `start_test` and `report_delivered`
//     are keyed to a session and must fire at most once per browser per
//     session. `oncePerSession()` gates them via localStorage; GA4's
//     `transaction_id` + Meta's `eventID` give the vendors a second,
//     cross-device dedup key (both use the session id).
//
// Event wiring lives at the funnel anchors, NOT here:
//   page_view       → src/routes/__root.tsx        (every client navigation)
//   start_test      → src/routes/session.$token.test.tsx   (test runner mount)
//   purchase        → src/routes/session.$token.tsx        (session first seen paid)
//   report_delivered→ src/routes/session.$token.result.tsx (report first rendered)

/* eslint-disable @typescript-eslint/no-explicit-any */

import { ADMIN_MUTE_KEY, isAnalyticsMuted } from "./mute";
import { stepKey, type StartStep } from "@/lib/start-funnel";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: any[]) => void;
    fbq?: ((...args: any[]) => void) & { queue?: unknown[]; loaded?: boolean };
    _fbq?: unknown;
  }
}

/**
 * Идентификатор GA4 по умолчанию — ПУБЛИЧНОЕ значение, оно и так уезжает в
 * браузер каждому посетителю.
 *
 * ПОЧЕМУ ЗАШИТ, А НЕ ТОЛЬКО ИЗ ПЕРЕМЕННОЙ. Модуль написан в июле и всё это
 * время был мёртв: переменная сборки не задана, ANALYTICS_ENABLED = false,
 * landingAnalyticsHtml возвращает пустую строку. Снаружи это выглядит как
 * работающая аналитика — код есть, вызовы стоят, — а событий нет ни одного.
 * Ровно так же был устроен клиентский DSN Sentry, и по той же причине зашит.
 *
 * Переменная сборки по-прежнему сильнее: понадобится второй поток (стенд,
 * отдельное свойство) — задаётся VITE_GA4_MEASUREMENT_ID и перебивает это.
 */
const DEFAULT_GA4_MEASUREMENT_ID = "G-JVPNP3TCE1";

// Build-time public IDs. Meta pixel остаётся строго из переменной: своего id у
// нас нет, а выдумывать его нельзя.
export const GA4_MEASUREMENT_ID =
  (import.meta.env.VITE_GA4_MEASUREMENT_ID as string | undefined)?.trim() ||
  DEFAULT_GA4_MEASUREMENT_ID;
export const META_PIXEL_ID = (import.meta.env.VITE_META_PIXEL_ID as string | undefined)?.trim();

export const ANALYTICS_ENABLED = Boolean(GA4_MEASUREMENT_ID || META_PIXEL_ID);

// ─── админ не считается (25.10.2026) ─────────────────────────────────────────
//
// /admin и вошедший админ в GA не попадают: владелец заходит в админку
// каждый день, и его визиты размывали бы и без того маленькие числа.
// Признак админа — флаг в localStorage: его ставит /admin после проверки роли
// (user_roles), снимает «Выйти». Флаг — только про аналитику, доступа он не даёт.
// Глушение — штатный выключатель GA `window['ga-disable-<ID>'] = true`: он
// гасит все вызовы gtag, включая ev() главной и событие статичных страниц.

export { ADMIN_MUTE_KEY, isAnalyticsMuted };

function applyGaDisable(muted: boolean): void {
  try {
    if (GA4_MEASUREMENT_ID) (window as any)[`ga-disable-${GA4_MEASUREMENT_ID}`] = muted;
  } catch {
    /* аналитика не роняет страницу */
  }
}

export function muteAnalyticsForAdmin(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(ADMIN_MUTE_KEY, "1");
  } catch {
    /* приватный режим — глушим хотя бы этот просмотр */
  }
  applyGaDisable(true);
}

export function unmuteAnalyticsForAdmin(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(ADMIN_MUTE_KEY);
  } catch {
    /* no-op */
  }
}

/** Тот же признак строкой JS — для тега в <head> (до загрузки gtag) и статичных страниц. */
const MUTE_SNIPPET = (id: string) =>
  `try{if(/^\\/admin(\\/|$)/.test(location.pathname)||localStorage.getItem(${JSON.stringify(ADMIN_MUTE_KEY)})==="1")window[${JSON.stringify(`ga-disable-${id}`)}]=true;}catch(_){}`;

// ─── head <script> tags (SSR-injected) ────────────────────────────────────────
//
// Returned in the shape TanStack Router's `head().scripts` expects: a flat
// object whose keys become element attributes, plus `children` for inline JS.
// Only vendors with a configured id are emitted. Neither snippet fires the
// vendor's built-in initial page-view — the client sends the first page_view /
// PageView from the router effect so SPA navigations and the first load are
// counted through one path (no double-count).

type HeadScript = { src?: string; async?: boolean; defer?: boolean; children?: string };

export function analyticsHeadScripts(): HeadScript[] {
  const scripts: HeadScript[] = [];

  if (GA4_MEASUREMENT_ID) {
    scripts.push({
      src: `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA4_MEASUREMENT_ID)}`,
      async: true,
    });
    scripts.push({
      children:
        MUTE_SNIPPET(GA4_MEASUREMENT_ID) +
        "window.dataLayer=window.dataLayer||[];" +
        "function gtag(){dataLayer.push(arguments);}" +
        "window.gtag=gtag;" +
        "gtag('js',new Date());" +
        // send_page_view:false — the router effect owns page_view so SPA
        // route changes are tracked, not just the first document load.
        `gtag('config',${JSON.stringify(GA4_MEASUREMENT_ID)},{send_page_view:false});`,
    });
  }

  if (META_PIXEL_ID) {
    scripts.push({
      children:
        "!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?" +
        "n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;" +
        "n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;" +
        "t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}" +
        "(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');" +
        // init only — PageView is sent from the router effect (see above).
        `fbq('init',${JSON.stringify(META_PIXEL_ID)});`,
    });
  }

  return scripts;
}

// ─── static-landing injection (serve-time) ───────────────────────────────────
//
// The marketing landings (public/methodology*.html) are served as raw HTML by
// src/routes/{index,az,en}.tsx — they never mount the React __root, so they
// miss analyticsHeadScripts() + the router page_view effect entirely. This
// serialises the SAME vendor snippets (single source of truth for the IDs) to
// a raw-HTML string the route splices into the page's <!--ANALYTICS--> slot,
// then adds a tiny bootstrap that (a) fires the first page_view/PageView — the
// vendor auto page-view is suppressed above — and (b) delegates cta_click from
// any [data-cta] element. All calls are window-guarded, so an unset env or a
// blocked vendor is a clean no-op. Returns "" when no vendor id is configured.

export function landingAnalyticsHtml(): string {
  if (!ANALYTICS_ENABLED) return "";
  const loaders = analyticsHeadScripts()
    .map((s) =>
      s.src
        ? `<script src="${s.src}"${s.async ? " async" : ""}${s.defer ? " defer" : ""}></script>`
        : `<script>${s.children ?? ""}</script>`,
    )
    .join("");
  const bootstrap =
    "<script>(function(){" +
    // Админ на статичной странице: pv/ev/cta молчат и в Pixel (GA глушит ga-disable из тега выше).
    `var muted=false;try{muted=/^\\/admin(\\/|$)/.test(location.pathname)||localStorage.getItem(${JSON.stringify(ADMIN_MUTE_KEY)})==="1";}catch(_){}if(muted)return;` +
    "function pv(){if(window.gtag)gtag('event','page_view',{page_path:location.pathname,page_location:location.href,page_title:document.title});if(window.fbq)fbq('track','PageView');}" +
    "pv();" +
    "document.addEventListener('click',function(e){var el=e.target&&e.target.closest?e.target.closest('[data-cta]'):null;if(!el)return;var id=el.getAttribute('data-cta')||'';if(window.gtag)gtag('event','cta_click',{cta_id:id});if(window.fbq)fbq('trackCustom','CtaClick',{cta_id:id});});" +
    // ── СЛЕПАЯ ЗОНА: мини-тест ──
    //
    // До сих пор мини-тест оставлял след ровно в одном случае — если человек в
    // конце вводил почту (POST /api/lead). За всё время таких один. Открытие и
    // прохождение не записывались нигде, а мини-тест — точка входа для всей
    // будущей рекламы: без этих двух событий нельзя сказать даже, доходят ли
    // люди до результата.
    //
    // Слушаем ЗДЕСЬ, а не в трёх статических файлах: разметка мини-теста в них
    // одинаковая, а правка в трёх местах разъезжается на первой же спешке.
    "function ev(n,p){if(window.gtag)gtag('event',n,p||{});if(window.fbq)fbq('trackCustom',n,p||{});}" +
    "var mtOpened=false;" +
    "function mtOpen(how){if(mtOpened)return;mtOpened=true;ev('mini_test_open',{entry:how});}" +
    "document.addEventListener('click',function(e){var el=e.target&&e.target.closest?e.target.closest('[data-open-minitest]'):null;if(el)mtOpen('click');});" +
    // Открытие по адресу (?mini=1 / #mini-test) — путь рекламы и шапки профиля.
    "try{var q=new URLSearchParams(location.search);if(q.get('mini')==='1'||location.hash==='#mini-test')mtOpen('url');}catch(_){}" +
    // Завершение ловим по снятию hidden с блока результата: так не приходится
    // влезать в саму логику опроса, и событие переживёт её переписывание.
    "try{var res=document.getElementById('mt-result');" +
    "if(res&&window.MutationObserver){var done=false;new MutationObserver(function(){" +
    "if(done||res.hasAttribute('hidden'))return;done=true;ev('mini_test_complete',{});" +
    "}).observe(res,{attributes:true,attributeFilter:['hidden']});}}catch(_){}" +
    "})();</script>";
  return loaders + bootstrap;
}

// ─── low-level vendor calls (guarded) ─────────────────────────────────────────

function ga(...args: any[]): void {
  if (typeof window === "undefined") return;
  if (typeof window.gtag !== "function") return;
  if (isAnalyticsMuted()) {
    applyGaDisable(true);
    return;
  }
  try {
    window.gtag(...args);
  } catch {
    /* analytics must never throw into product code */
  }
}

function fb(...args: any[]): void {
  if (typeof window === "undefined") return;
  if (typeof window.fbq !== "function") return;
  if (isAnalyticsMuted()) return;
  try {
    window.fbq(...args);
  } catch {
    /* analytics must never throw into product code */
  }
}

// ─── fire-once gate ───────────────────────────────────────────────────────────
//
// Returns true the FIRST time it sees `key` in this browser, false thereafter.
// On any storage failure (Safari private mode, quota, sandboxed iframe) it
// returns true — better to let the event through and rely on the vendor's
// transaction_id / eventID dedup than to silently drop a conversion.

export function oncePerSession(key: string): boolean {
  if (typeof window === "undefined") return false;
  const storageKey = `yt_evt_${key}`;
  try {
    if (window.localStorage.getItem(storageKey)) return false;
    window.localStorage.setItem(storageKey, "1");
    return true;
  } catch {
    return true;
  }
}

// ─── event trackers ───────────────────────────────────────────────────────────

/** Every client navigation, including the first paint. */
export function trackPageView(path: string): void {
  ga("event", "page_view", {
    page_path: path,
    page_location: typeof window !== "undefined" ? window.location.href : undefined,
    page_title: typeof document !== "undefined" ? document.title : undefined,
  });
  fb("track", "PageView");
}

/**
 * Анкета отправлена, сессия создана — вход в платящий путь.
 *
 * До сих пор этого события не существовало: факт был только в базе, а в
 * аналитике воронка начиналась сразу с оплаты. Сравнивать «сколько пришло» и
 * «сколько дошло» было не с чем.
 */
export function trackFormSubmitted(params: {
  ageGroup: string;
  language: string;
  tier: string;
}): void {
  ga("event", "form_submitted", {
    age_group: params.ageGroup,
    language: params.language,
    tier: params.tier,
  });
  fb("trackCustom", "FormSubmitted", {
    age_group: params.ageGroup,
    language: params.language,
    tier: params.tier,
  });
}

/**
 * Нажата оплата — последнее, что мы видим до ухода на страницу банка.
 *
 * Без параметров намеренно: на этом шаге у нас нет ничего, что не было бы уже
 * отправлено с form_submitted, а токен сессии третьей стороне не уходит
 * никогда — по нему открывается отчёт.
 */
export function trackPayClick(): void {
  ga("event", "pay_click", {});
  fb("trackCustom", "PayClick", {});
}

/** The child began answering the assessment. */
export function trackStartTest(params: {
  sessionId: string;
  ageGroup: string;
  language: string;
}): void {
  ga("event", "start_test", {
    session_id: params.sessionId,
    age_group: params.ageGroup,
    language: params.language,
  });
  fb("trackCustom", "StartTest", {
    age_group: params.ageGroup,
    language: params.language,
  });
}

/**
 * A real, completed payment. Callers MUST gate on a genuine paid session
 * (amount > 0) before calling — never fire from a preview / demo / comped
 * (partner-code) session. `transactionId` is the session id: it is GA4's
 * `transaction_id` and Meta's `eventID`, giving both vendors cross-device
 * dedup on top of the local oncePerSession() gate.
 */
export function trackPurchase(params: {
  transactionId: string;
  value: number;
  currency: string;
  tier: string;
}): void {
  const currency = purchaseCurrency(params.currency);
  ga("event", "purchase", {
    transaction_id: params.transactionId,
    value: params.value,
    currency,
    items: [{ item_id: params.tier, item_name: `Yolutap ${params.tier}`, price: params.value }],
  });
  fb(
    "track",
    "Purchase",
    { value: params.value, currency, content_type: params.tier },
    { eventID: params.transactionId },
  );
}

/**
 * Валюта для GA — только буквенный код ISO 4217 (25.10.2026). В старых
 * оплаченных сессиях лежит «944» (числовой код маната, до нормализации на
 * входе callback), GA такой код не принимает и покупку отбрасывает.
 */
export function purchaseCurrency(raw: string | null | undefined): string {
  const v = (raw ?? "").trim().toUpperCase();
  if (!v || v === "944") return "AZN";
  return /^[A-Z]{3}$/.test(v) ? v : "AZN";
}

// ─── события воронки для владельца (25.10.2026) ───────────────────────────────
//
// Новые имена — рядом со старыми (form_submitted, start_test, partner_join_sent
// остаются как были: на них могут держаться отчёты GA). Ничего, кроме
// возрастной группы, языка и тарифа, не уходит: ни токена, ни имени, ни почты.

/** test_start — сессия теста создана (анкета /start отправлена, до оплаты). */
export function trackTestStart(params: { ageGroup: string; language: string; tier: string }): void {
  ga("event", "test_start", { age_group: params.ageGroup, language: params.language, tier: params.tier });
}

// ─── воронка /start по шагам (28.10.2026, src/lib/start-funnel.ts) ────────────
//
// Один раз на шаг за визит: набор сбрасывается, когда /start открывают заново
// (resetStartSteps в начале страницы), экран оплаты — один раз на сессию.
// «Далее» считается отдельно для ok=true и ok=false, ошибка — по коду.
const startSeen = new Set<string>();

export function resetStartSteps(): void {
  startSeen.clear();
}

export function trackStartStep(
  step: StartStep,
  params: { lang: string; pkg: string; ok?: boolean; reason?: string; code?: string; preset_lang?: string; preset_age?: string },
): void {
  const key = stepKey(step, params);
  if (startSeen.has(key)) return;
  startSeen.add(key);
  ga("event", step, params);
}

/** test_complete — ребёнок ответил на последний вопрос, сервер поставил complete. */
export function trackTestComplete(params: { ageGroup: string; language: string }): void {
  ga("event", "test_complete", { age_group: params.ageGroup, language: params.language });
}

/** google_reviews_click — нажали значок «★ 5,0 · 24 rəy Google-da» (27.10.2026). */
export function trackGoogleReviewsClick(params: { placement: string; language: string }): void {
  ga("event", "google_reviews_click", { placement: params.placement, language: params.language });
}

/** tutor_signup — анкета репетитора или кружка отправлена и принята сервером. */
export function trackTutorSignup(params: { kind: string; lang: string }): void {
  ga("event", "tutor_signup", { partner_kind: params.kind, language: params.lang });
}

/**
 * The finished report was delivered to (rendered for) the user. No session
 * identifier is sent — the result route is keyed only by the secret access
 * token, which must never leave for a third-party analytics endpoint; the
 * fire-once dedup is done locally by the caller.
 */
export function trackReportDelivered(params: { ageGroup: string; language: string }): void {
  ga("event", "report_delivered", {
    age_group: params.ageGroup,
    language: params.language,
  });
  fb("trackCustom", "ReportDelivered", {
    age_group: params.ageGroup,
    language: params.language,
  });
}
